// src/views/UserViews/insights/components/WorkZoneAdaptCard.tsx
//
// Work Zone Breaches table (PRD D8). Real data source as of M4: `InsightsDAO.
// getWorkZoneBreaches`, fetched centrally by `useInsightsData` alongside every other
// Insights section and handed to this card as `breaches: InsightsSectionState<
// WorkZoneBreachQueryResult>` - the same loading/error/data lifecycle every sibling section
// on this page already uses (see FleetStatusSection.tsx / DeviceStatusDonut.tsx). Before M4
// this card took no props and rendered a permanent empty state; that was accurate at the
// time (no per-event history existed anywhere in the pipeline - see the Work Zone Breach
// PRD §1, gaps 1-3) and is no longer true now that `gold.work_zone_breach` and its Cube
// model exist (design spec 2026-09-10-work-zone-sustained-alert-runs-design.md).
//
// Scope is deliberately narrow (PRD D10 - "no new card, no new empty-state copy, no
// exposure columns"): this was originally the same three-column table - Time, Location,
// Group - with the same title and the same empty-state copy/testid as before. Nothing about
// the table's shape changed at that time; only its data source did.
//
// AMENDED 2026-09-15: the three-column claim above is now out of date, not wrong at the time
// it was written - D10's copy freeze was explicitly and separately amended (PRD D5, this same
// day) to add exactly one new column, User, per the user-attribution feature. The table is
// four columns as of this change (Time, User, Location, Group) and D10's freeze otherwise
// still holds: no exposure/denominator columns, no fifth column, same title, same
// empty-state copy/testid.
//
// AMENDED 2026-09-16: D10's "no new strings on this card" freeze is overridden again, same
// class of amendment as the User column, for an owner-requested collapse/expand on this
// table. Default view is the 5 latest already-fetched rows (UI collapse only - not the DAO
// WORK_ZONE_BREACH_ROW_LIMIT of 500). A "Show N more" / "Show less" toggle reveals or
// re-hides the rest of the fetched set in one shot - not chunked paging, and not a fetch.
// Empty-state copy and testid stay frozen. Silent `_truncated` handling of the 500 cap is
// unchanged: expand does not fetch more and does not add "showing first N of M" copy.
//
// Group (this column) and User (new column, added 2026-09-15 - M4 user-attribution
// feature): the claim below that Group must render empty is now FACTUALLY WRONG and is
// AMENDED rather than deleted, per the same "wrong developer documentation is a defect in
// its own right" reasoning the prior PRD's D10 used to authorize updating this exact
// comment before. Original claim, preserved: there is no group/crew/team field anywhere in
// the telemetry WIRE payload - the identity set the wire actually carries is org_code /
// auth_id / c_mac / c_hw_id / is_core / cfg_id / schema_ver, and nothing else (PRD D12).
// That is still true and is exactly why Group cannot be derived from telemetry itself.
//
// What changed: `breach.authId` is resolved against sources external to telemetry - see
// `workZoneBreachUserResolver.ts`'s own header comment for the full lookup design. In short: the
// plan/PRD's original design (D9) called for a single resolver over `OrganizationProvider`'s
// `groups` state, but that state was measured on 2026-09-15 to be missing the `Default`
// group entirely for a real org (13 of 14 groups, 40 of 63 users) - `Default` holds 20 of
// that org's 21 ops users, i.e. almost every actual device wearer. The primary User source
// remains `getAllOrgCompassDevices()`, and the primary Group source remains
// `getUsersGroup(organization, authId)`. The already-loaded `groups[].group_value` payload is
// now an in-memory fallback for a missing device display name and, when needed, group name.
// It never triggers a second member fetch. The `Default`-group bug ITSELF is not fixed here.
//
// D12's binding prohibition is UNCHANGED and remains fully in force: do NOT derive a group
// from `cfg_id`, device clustering, `org_code` substructure, or co-location - populating
// Group still requires an EXTERNAL membership source, and `getUsersGroup` is exactly that,
// not a telemetry-side proxy. See the render-site comment on the User/Group cells below for
// the same warning restated at the point a future reader is most likely to be tempted to
// "fix" either cell with a telemetry-derived shortcut.
//
// FIXED 2026-09-15/observed, patched 2026-09-16: for its first day in production this card's
// Group cell rendered the User column's OWN two fallback strings (INSIGHTS_WORK_ZONE_ADAPT_
// NO_USER_RECORDED / _USER_UNRESOLVABLE) whenever a row's group half was empty - i.e. a row
// with no recorded user rendered the literal text "No user recorded" in BOTH the User AND
// the Group column. That was dormant (every live row happened to resolve a group) until an
// org with zero resolvable groups was observed, at which point a Group cell answering a
// question about the USER became visible. `formatBreachGroupCell` below now uses its OWN
// pair of fallback strings (INSIGHTS_WORK_ZONE_ADAPT_GROUP_NO_USER / _GROUP_UNRESOLVABLE -
// see StringKeys.js and that function's own doc comment) rather than the User column's -
// see that function for the corrected fallback-string rationale.
//
// Location (nullable): `location === null` renders the dedicated "no location reported"
// string (INSIGHTS_WORK_ZONE_ADAPT_NO_LOCATION, the one new StringKey this card adds - see
// M4 task 30) - it is an EXERCISED PRIVACY CHOICE, not a bug and not a data-quality defect.
// Location sharing is user-controlled on the device/app side (PRD D11/§7), and the measured
// null rate is 35.71% of source rows fleet-wide, up to ~99.98% in one org (PRD, "Measured
// fact you must design around") - null location is the COMMON case for this UI, not an
// edge case, and is treated as first-class rendering, not a fallback branch. A present
// location renders as plain decimal-degree text (fixed precision - see
// formatWorkZoneBreachLocation below); it is NEVER "0, 0" as a stand-in for absence, because
// 0 is a legal latitude AND a legal longitude (the DAO's own buildWorkZoneBreachLocation
// already guards this - see InsightsDAO.ts). Never an empty cell either, in either case: an
// empty Location cell would be visually indistinguishable from the intentionally-empty Group
// cell, which would make a genuine privacy choice look like a missing/broken feature (and
// vice versa muddy the meaning of Group's own emptiness) - see PRD R15.
//
// REVERSED 2026-09-15: the sentence above originally continued "...no map link, no embedded
// map, no reverse geocoding - PRD NG7," and PRD NG7 did read that way when this card first
// shipped. That ruling is now reversed by owner decision: the rendered coordinate text is
// wrapped in a Google Maps link (buildWorkZoneBreachMapUrl below) whenever a location is
// present, matching the same `https://maps.google.com/?q=<lat>,<lng>` link already used in
// three other places in this app (ConnectionLossMarker.jsx, UserDashboardHeader.jsx,
// ChatSideBar.tsx) plus EmergencyHistoryReport.tsx's report-side equivalent - this card was
// becoming the one place in the product that showed a coordinate with no way to open it on
// a map, not a place with a principled reason to withhold one. Embedded maps and reverse
// geocoding remain out of scope; only the link-out changed. See the PRD's amended NG7/D11
// and design spec section 11.3 for the full record of the reversal, including the follow-on
// privacy question this decision explicitly does NOT pre-approve: when the user-attribution
// feature's name column eventually lands on this same card, the COMBINATION of (name +
// precise coordinates + one-tap mapping) is a distinct privacy question to be re-reviewed at
// that time, not one this change already settled.
//
// Time: the cell shows the VIEWER's own local time (formatWorkZoneBreachLocalTime below),
// with the full UTC-formatted string available on hover (formatWorkZoneBreachTime, now a
// tooltip rather than the primary display). REVERSED 2026-09-15 by owner decision - this
// paragraph originally read "startTsUtc rendered in UTC ... rendering this card's Time
// column in local time instead would be the one inconsistent timestamp on an otherwise
// UTC-only page," which was the shipped, correct-at-the-time rationale. The reversal's
// actual rationale: local time is how every OTHER timestamp in this app renders to a viewer
// (see e.g. UserDashboardHeader's formatTimeHour / formatLastSeenAt) - UTC-only was this
// card's own inconsistency with the rest of the product, not the reverse. Both formatters
// still share a single instant-parsing helper (parseWorkZoneBreachInstant below) for the
// real parsing trap this card has to guard against that no other Insights field has hit
// before - see that helper's own comment for the trap itself, which this reversal leaves
// completely unchanged.
//
// The empty state below (work-zone-adapt-empty) is UNCHANGED from before M4, including its
// copy and testid (PRD D10/AR4) - it still literally says reporting is "not available yet",
// which is now slightly odd once an org genuinely has zero qualifying breaches rather than
// zero reporting capability, but that mismatch is a closed, accepted risk (AR4), not a
// defect to quietly fix here. AMENDED 2026-09-16: empty-state copy and testid stay frozen
// through the owner-requested collapse/expand; that change adds only the two toggle strings
// on the populated-data branch, never this empty-state branch.

import { useState } from 'react';
import {
  Button,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import type { InsightsSectionState } from '@/hooks/insights/useInsightsData';
import type { WorkZoneBreachLocation, WorkZoneBreachQueryResult } from '@/models/Insights/InsightsTypes';
import {
  resolveWorkZoneBreachUser,
  type BreachGroupMemberLookup,
  type GetUsersGroupFn,
  type WorkZoneBreachUserResolution,
} from '@/models/Insights/workZoneBreachUserResolver';
import type { OrganizationOnAzure, MiniOrganization } from '@/../generated-client/models';
import SectionCard from './SectionCard';

// Time / User / Location / Group (PRD OQ4, signed order and header strings - added
// 2026-09-15, M4 user-attribution feature). COLUMN_COUNT is the only structural change the
// empty-state row needs (its colspan tracks this constant, not a hardcoded number).
const COLUMN_COUNT = 4;
const LOADING_SKELETON_HEIGHT = 160;

/** Rows shown before the "Show N more" reveal. UI collapse only — not the DAO
 * WORK_ZONE_BREACH_ROW_LIMIT (500) cap. Expand reveals already-fetched rows; it does
 * not fetch more. */
const COLLAPSED_ROW_LIMIT = 5;

/** Decimal-degree precision for a rendered coordinate - 4 places, roughly 11m of resolution
 * at the equator. Matches this card's own pre-M4 placeholder format ("40.7128 N, 74.0060 W",
 * see git history) rather than an arbitrarily different value: that format was already this
 * card's stated intent for real data, and 4 places is in the right neighborhood for typical
 * phone-GPS accuracy (accuracyMeters) without implying more precision than the fix actually
 * supports (design spec section 11.3 - "do not round so far that the coordinate implies less
 * precision than accuracyMeters supports", and the inverse: do not print raw float noise
 * either). */
const COORDINATE_DECIMAL_PLACES = 4;

/** One coordinate axis as "<degrees> <hemisphere letter>". Math.abs plus a sign-derived
 * letter reads more like a coordinate a person would recognize than a signed decimal, and
 * matches the pre-M4 placeholder format this card already committed to (see module header).
 * `value >= 0` (not `value > 0`) is deliberate: latitude/longitude 0 is a real, legal
 * position (the equator / the prime meridian) and must render as "0.0000 N" / "0.0000 E",
 * never fall through to a negative-hemisphere letter or, worse, be treated as absent - see
 * the DAO's own isFiniteCoordinate guard (InsightsDAO.ts) for the upstream half of this same
 * correctness requirement. */
function formatCoordinateAxis(value: number, positiveLetter: string, negativeLetter: string): string {
  const degrees = Math.abs(value).toFixed(COORDINATE_DECIMAL_PLACES);
  const hemisphereLetter = value >= 0 ? positiveLetter : negativeLetter;
  return `${degrees}\u00B0 ${hemisphereLetter}`;
}

/**
 * Renders the Location cell's TEXT content for one breach (PRD R15; design section 11.3).
 * Exported for direct unit testing of the null/zero/real-coordinate branches without
 * needing a full component render.
 *
 *  - location === null -> the dedicated "no location reported" string. This is a real,
 *    exercised privacy choice (or a discarded 0,0 sentinel, or a momentary no-fix - the
 *    mart cannot and does not distinguish the three, see design section 4.4/11.2), not an
 *    error.
 *  - location present -> plain decimal-degree text, unchanged by this function itself.
 *    REVERSED 2026-09-15: this docstring originally continued "No map link, no embedded
 *    map, no reverse geocoding (PRD NG7) - a coordinate in a table cell is the entire
 *    scope," which was accurate when this function was the whole Location-rendering story.
 *    It no longer is: the render site now separately wraps this function's returned text in
 *    a Google Maps link when one is available (see buildWorkZoneBreachMapUrl below, and
 *    this file's header Location paragraph for the full rationale). This function's OWN
 *    signature and behavior are deliberately untouched by that change - it still returns a
 *    plain string, never JSX, so the five tests asserting its exact string output keep
 *    proving exactly what they always proved.
 *
 * NEVER returns an empty string in either branch - see this file's header comment for why
 * an empty Location cell would be indistinguishable from the intentionally-empty Group
 * cell.
 */
export function formatWorkZoneBreachLocation(
  location: WorkZoneBreachLocation | null,
  // Plain function-type annotation rather than importing `TFunction` - matches this same
  // directory's `FirmwareDetailsDialog.tsx` convention for a `t` parameter passed into a
  // plain (non-hook) helper function.
  t: (key: string) => string,
): string {
  if (location === null) {
    return t(StringKeys.INSIGHTS_WORK_ZONE_ADAPT_NO_LOCATION);
  }

  const latitudeText = formatCoordinateAxis(location.latitude, 'N', 'S');
  const longitudeText = formatCoordinateAxis(location.longitude, 'E', 'W');
  return `${latitudeText}, ${longitudeText}`;
}

/**
 * Renders the User cell's text for one breach's resolution (added 2026-09-15 - M4 user-
 * attribution feature). Exported for direct unit testing of all three resolution states
 * without needing a full component render, matching `formatWorkZoneBreachLocation`'s own
 * pattern above.
 *
 *  - `no-user-recorded` -> the dedicated "no user recorded" string (authId === '').
 *  - `unresolvable` -> the dedicated "unable to identify user" string - a DIFFERENT string
 *    from the one above, deliberately (PRD OQ1): one is a pipeline-coverage fact, the other
 *    a roster/device fact, and collapsing them would hide which failure actually occurred.
 *  - `resolved` -> `resolution.name` when present; otherwise the SAME "unable to identify
 *    user" string as the `unresolvable` branch - a resolved breach with no name is a
 *    legitimate partial resolution (PRD D8/D9-amended), not an error, but the cell still
 *    needs a real fallback string, never `''` and never `breach.authId` itself.
 *  - `identifyingUser` replaces that final fallback while the group-member source is loading,
 *    preventing a transient "unable" result before the fallback roster has finished loading.
 *    It never overrides a real name or the `no-user-recorded` state.
 *
 * NEVER returns an empty string on any branch, and NEVER returns the raw `authId` - see this
 * file's header comment (PRD D8/R14).
 */
export function formatBreachUserCell(
  resolution: WorkZoneBreachUserResolution,
  t: (key: string) => string,
  identifyingUser = false,
): string {
  switch (resolution.kind) {
    case 'no-user-recorded':
      return t(StringKeys.INSIGHTS_WORK_ZONE_ADAPT_NO_USER_RECORDED);
    case 'unresolvable':
      return t(
        identifyingUser
          ? StringKeys.INSIGHTS_WORK_ZONE_ADAPT_IDENTIFYING_USER
          : StringKeys.INSIGHTS_WORK_ZONE_ADAPT_USER_UNRESOLVABLE,
      );
    case 'resolved':
      return resolution.name ?? t(
        identifyingUser
          ? StringKeys.INSIGHTS_WORK_ZONE_ADAPT_IDENTIFYING_USER
          : StringKeys.INSIGHTS_WORK_ZONE_ADAPT_USER_UNRESOLVABLE,
      );
  }
}

/**
 * Renders the Group cell's text for one breach's resolution (added 2026-09-15).
 *
 * AMENDED 2026-09-16 (production bug, dormant since 2026-09-15 - see this file's header
 * comment): the paragraph immediately below, claiming this function "mirrors
 * `formatBreachUserCell` exactly" using the SAME pair of fallback strings, is now FACTUALLY
 * WRONG and is amended rather than deleted, per this file's own "wrong developer
 * documentation is a defect in its own right" reasoning (see module header). It was wrong
 * from the moment it was written, not merely outdated: a Group cell rendering the literal
 * text "No user recorded" answers a question about the USER, not the group, which is a
 * category error regardless of how many rows happened to mask it. Group now has its OWN pair
 * of fallback strings - INSIGHTS_WORK_ZONE_ADAPT_GROUP_NO_USER (mirrors `no-user-recorded`:
 * there was no `authId` to look a group up FOR at all - a pipeline-coverage fact, nothing to
 * look up, not a lookup failure) and INSIGHTS_WORK_ZONE_ADAPT_GROUP_UNRESOLVABLE (mirrors
 * `unresolvable`, and the group-null half of a partial `resolved` outcome: an `authId` DID
 * exist, but this organization's group roster has no entry for it - a roster fact,
 * independent of whether the name half of that same partial resolution separately
 * succeeded). See StringKeys.js for both keys' own comments.
 *
 * Original paragraph, preserved for the record (superseded by the above - do not follow
 * it): "Mirrors `formatBreachUserCell` above exactly, branch for branch, using
 * `resolution.group` in place of `resolution.name` - the two cells share one resolution
 * outcome and one pair of fallback strings by design: there is no state where the user
 * resolves but the group lookup fails for a DIFFERENT reason than 'this half of the
 * two-source lookup came up empty,' so a separate set of Group-specific fallback strings
 * would imply an independent failure mode that cannot occur (mirrors the original
 * single-source design's own fallback-taxonomy reasoning, unchanged by the two-source
 * amendment)." That reasoning conflated "the two cells describe the same underlying
 * resolution outcome" (true) with "the two cells may therefore share display strings"
 * (false) - a Group cell and a User cell answer two different questions about the same row,
 * even on the rows where both happen to be empty for related reasons.
 *
 * NEVER returns an empty string on any branch, NEVER returns the raw `authId`, and NEVER
 * returns one of the User column's own two fallback strings (this file's header comment;
 * StringKeys.js).
 */
export function formatBreachGroupCell(
  resolution: WorkZoneBreachUserResolution,
  t: (key: string) => string,
): string {
  switch (resolution.kind) {
    case 'no-user-recorded':
      return t(StringKeys.INSIGHTS_WORK_ZONE_ADAPT_GROUP_NO_USER);
    case 'unresolvable':
      return t(StringKeys.INSIGHTS_WORK_ZONE_ADAPT_GROUP_UNRESOLVABLE);
    case 'resolved':
      return resolution.group ?? t(StringKeys.INSIGHTS_WORK_ZONE_ADAPT_GROUP_UNRESOLVABLE);
  }
}

/**
 * Builds the Google Maps deep link URL for a breach's location (added 2026-09-15 - see this
 * file's header Location paragraph for the PRD NG7 reversal this implements). Matches the
 * `https://maps.google.com/?q=<lat>,<lng>` convention already used by
 * ConnectionLossMarker.jsx / UserDashboardHeader.jsx / ChatSideBar.tsx elsewhere in this
 * app, rather than inventing a different URL form (e.g. the `api=1` variant).
 *
 * Deliberately takes the RAW signed decimal `location`, never `formatWorkZoneBreachLocation`
 * above's hemisphere-lettered display string - "40.7128\u00B0 N, 74.0060\u00B0 W" does not
 * parse as a Maps query, and a bare positive number for a "W" longitude would put the pin in
 * the wrong hemisphere entirely. EmergencyHistoryReport.tsx already keeps this exact split
 * (raw signed decimal in the href, `.toFixed()`-formatted text in the visible label); this
 * mirrors that split rather than inventing a new one.
 *
 * `location === null` -> null (nothing to link to - mirrors formatWorkZoneBreachLocation's
 * own null branch). Latitude/longitude `0` are real, legal positions (the equator / the
 * prime meridian), not sentinels for "absent" - see formatCoordinateAxis's own comment for
 * the upstream half of this same correctness requirement. This function tests `location`
 * itself against `null`, never `location.latitude` or `location.longitude` against falsy,
 * so a `0` axis value is never mistaken for an absent one.
 */
export function buildWorkZoneBreachMapUrl(
  location: WorkZoneBreachLocation | null,
): string | null {
  if (location === null) {
    return null;
  }

  return `https://maps.google.com/?q=${location.latitude},${location.longitude}`;
}

/**
 * Parses `WorkZoneBreach.startTsUtc` into the JS `Date` instant it represents. This is the
 * SINGLE place the zone-parsing logic lives - both formatWorkZoneBreachTime (UTC) and
 * formatWorkZoneBreachLocalTime (viewer local time) below call this rather than each
 * reimplementing it, so the trap this comment describes can only be fixed (or broken) once.
 *
 * A REAL parsing trap this field hits that no other Insights field has: startTsUtc's actual
 * runtime shape coming out of getWorkZoneBreaches (InsightsDAO.ts) is a Cube/ClickHouse
 * DateTime64 string with NO trailing Z or offset (confirmed against that function's own test
 * fixtures, e.g. "2026-09-01T12:00:00.000") - even though the underlying gold column is
 * DateTime64(3, 'UTC') and the value genuinely IS a UTC instant. new Date(startTsUtc) on a
 * zone-less string is parsed as LOCAL time by the JS runtime, not UTC - this is exactly the
 * UTC-instant/local-timezone trap getEarliestDataDate's own comment calls "Trap 2", just
 * hitting a full timestamp instead of a bare calendar date. Appending a Z when the string
 * doesn't already carry an explicit zone marker is what makes this parse correctly
 * regardless of the viewer's own offset. This guard is unchanged by the 2026-09-15
 * local-time reversal below - it protects the INSTANT being parsed, which is independent of
 * which timezone that instant is later displayed in.
 */
function parseWorkZoneBreachInstant(startTsUtc: string): Date {
  const hasExplicitZoneMarker = /Z$|[+-]\d{2}:?\d{2}$/.test(startTsUtc);
  return new Date(hasExplicitZoneMarker ? startTsUtc : `${startTsUtc}Z`);
}

/** Format options shared by the UTC and local-time formatters below - only `timeZone`
 * differs between the two. `timeZoneName: 'short'` lets Intl itself append the zone
 * abbreviation ("UTC", "PDT", ...) to the formatted string, rather than this file
 * concatenating a hardcoded literal - the latter would be a bare string literal in rendered
 * output, which StringKeys discipline (M4 task 30) forbids outside the permitted new keys.
 * This is the same category as toLocaleString's own locale-supplied commas/AM-PM markers
 * elsewhere in the app (see formatLastSeenAt.ts) - platform-supplied text, not an
 * author-written literal. */
const WORK_ZONE_BREACH_TIME_FORMAT_BASE: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
};

/** The UTC formatter additionally forces `timeZone: 'UTC'`; the local formatter (below)
 * deliberately omits `timeZone` so `toLocaleString` falls back to the runtime's own zone. */
const WORK_ZONE_BREACH_TIME_FORMAT_UTC: Intl.DateTimeFormatOptions = {
  ...WORK_ZONE_BREACH_TIME_FORMAT_BASE,
  timeZone: 'UTC',
};

/**
 * Formats WorkZoneBreach.startTsUtc in UTC. Used for the Time column's hover tooltip only as
 * of 2026-09-15 (see this file's header Time paragraph for the reversal) - it is no longer
 * the primary, always-visible Time column text, but stays a named formatter (rather than
 * being inlined at the tooltip call site) because it is still independently exercised by
 * this file's own zone-less-parse tests below, and because "the exact UTC instant,
 * unambiguously labeled" is real, distinct information from "the viewer's local time," not a
 * duplicate of it.
 */
export function formatWorkZoneBreachTime(startTsUtc: string): string {
  return parseWorkZoneBreachInstant(startTsUtc).toLocaleString(
    undefined,
    WORK_ZONE_BREACH_TIME_FORMAT_UTC,
  );
}

/**
 * Formats WorkZoneBreach.startTsUtc in the VIEWER's own local time zone (added 2026-09-15).
 * This is now the Time column's primary, always-visible text - see this file's header Time
 * paragraph for why local time replaced UTC as the default here. Shares
 * parseWorkZoneBreachInstant with formatWorkZoneBreachTime above, so the zone-less-DateTime64
 * parsing trap is guarded exactly once for both formatters, not duplicated.
 */
export function formatWorkZoneBreachLocalTime(startTsUtc: string): string {
  return parseWorkZoneBreachInstant(startTsUtc).toLocaleString(
    undefined,
    WORK_ZONE_BREACH_TIME_FORMAT_BASE,
  );
}

type WorkZoneAdaptCardProps = {
  breaches: InsightsSectionState<WorkZoneBreachQueryResult>;
  /** Primary NAME source (added 2026-09-15) - `Map<last_auth_id, last_user_display_name>` built in
   * `Insights.tsx` from `getAllOrgCompassDevices()`. Built and fetched by the caller, never
   * here: this card stays a pure presentation component, matching every sibling Insights
   * section (plan P-5/P-5a). See `workZoneBreachUserResolver.ts`'s header comment for why
   * this remains the first lookup before the loaded group-member fallback. */
  deviceNameMap: Map<string, string | null>;
  /** Pre-indexed fallback from the already-loaded `groups[].group_value` payload. */
  groupMemberMap: Map<string, BreachGroupMemberLookup>;
  groupsStatus: 'idle' | 'loading' | 'success' | 'error';
  /** GROUP source (added 2026-09-15) - passed through unchanged from `OrganizationProvider`,
   * already in scope on `Insights.tsx`. Nullable because this page tolerates a null
   * organization elsewhere (see `Insights.tsx`'s own `UserDashboard` gating comment); a null
   * organization simply means every row's group half resolves to "unable to identify user"
   * until it loads. */
  organization: OrganizationOnAzure | MiniOrganization | null;
  /** `OrganizationProvider.getUsersGroup`, passed through as a plain function reference so
   * this card never imports the organization context directly (see
   * `workZoneBreachUserResolver.ts`'s `GetUsersGroupFn` for why this stays an injected
   * parameter rather than an import). */
  getUsersGroup: GetUsersGroupFn;
};

export default function WorkZoneAdaptCard({
  breaches,
  deviceNameMap,
  groupMemberMap,
  groupsStatus,
  organization,
  getUsersGroup,
}: WorkZoneAdaptCardProps) {
  const { t } = useTranslation();
  const { data, loading, error } = breaches;
  const rows = data?.breaches ?? [];
  // Key expand off row-set identity (length + first id) so a date-range refetch
  // collapses the table without a setState-in-effect reset.
  const rowSetIdentity = `${rows.length}:${rows[0]?.id ?? ''}`;
  const [expandedIdentity, setExpandedIdentity] = useState<string | null>(null);
  const expanded = expandedIdentity === rowSetIdentity;

  const visibleRows = expanded ? rows : rows.slice(0, COLLAPSED_ROW_LIMIT);
  const hiddenCount = rows.length - visibleRows.length;
  // Independent of `expanded`: the toggle (now "Show less") must stay rendered once
  // expanded even though hiddenCount is 0.
  const canToggle = rows.length > COLLAPSED_ROW_LIMIT;

  // data?.truncated (design section 11.2) is read here, per normal hygiene, and
  // deliberately never wired into any rendered output - Conflict B of this milestone's
  // task list. D10 forbids introducing new user-facing copy on this card (e.g. a "showing
  // first N of M breaches" line), so v1 accepts silent truncation past
  // WORK_ZONE_BREACH_ROW_LIMIT rather than inventing copy to disclose it. Named
  // `_truncated` (see eslint config's varsIgnorePattern: '^_') to document the deliberate
  // non-use at the exact point it is consumed, rather than leaving an unexplained unused
  // field for a future reader to wonder about. The 2026-09-16 "Show N more" toggle is a
  // separate owner-requested string (UI collapse of already-fetched rows), not truncation
  // disclosure of the 500 cap.
  const _truncated = data?.truncated ?? false;
  const breachDefinition = t(StringKeys.INSIGHTS_WORK_ZONE_ADAPT_BREACH_DEFINITION);

  return (
    <SectionCard
      title={t(StringKeys.INSIGHTS_WORK_ZONE_ADAPT_TITLE)}
      titleAdornment={
        <Tooltip title={breachDefinition} placement="top" arrow>
          <InfoOutlinedIcon
            aria-label={breachDefinition}
            tabIndex={0}
            sx={{ fontSize: '1rem', color: 'text.secondary' }}
          />
        </Tooltip>
      }
    >
      {loading && (
        <Skeleton
          variant="rectangular"
          height={LOADING_SKELETON_HEIGHT}
          data-testid="work-zone-adapt-loading"
        />
      )}

      {!loading && error && (
        <Typography variant="body2" color="error" data-testid="work-zone-adapt-error">
          {t(StringKeys.INSIGHTS_SECTION_LOAD_ERROR)}
        </Typography>
      )}

      {!loading && !error && data && (
        <>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t(StringKeys.INSIGHTS_WORK_ZONE_ADAPT_COLUMN_TIME)}</TableCell>
              <TableCell>{t(StringKeys.INSIGHTS_WORK_ZONE_ADAPT_COLUMN_USER)}</TableCell>
              <TableCell>{t(StringKeys.INSIGHTS_WORK_ZONE_ADAPT_COLUMN_LOCATION)}</TableCell>
              <TableCell>{t(StringKeys.INSIGHTS_WORK_ZONE_ADAPT_COLUMN_GROUP)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    data-testid="work-zone-adapt-empty"
                  >
                    {t(StringKeys.INSIGHTS_WORK_ZONE_ADAPT_EMPTY)}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((breach) => {
                const locationText = formatWorkZoneBreachLocation(breach.location, t);
                const mapUrl = buildWorkZoneBreachMapUrl(breach.location);
                const openInMapsLabel = t(StringKeys.INSIGHTS_WORK_ZONE_ADAPT_OPEN_IN_MAPS);
                // Multi-source resolution - see workZoneBreachUserResolver.ts for lookup order.
                // The pre-indexed group-member map is an in-memory fallback only; no row fetches.
                // `breach.authId` is consumed HERE ONLY, as an input to a pure lookup - it is
                // never itself rendered, in this cell or anywhere else on this row (PRD D8/
                // R14/S7).
                const userResolution = resolveWorkZoneBreachUser(
                  breach.authId,
                  deviceNameMap,
                  organization,
                  getUsersGroup,
                  groupMemberMap,
                );
                const identifyingUser =
                  groupsStatus === 'loading' &&
                  breach.authId !== '' &&
                  (userResolution.kind === 'unresolvable' ||
                    (userResolution.kind === 'resolved' && userResolution.name === null));
                const userText = formatBreachUserCell(userResolution, t, identifyingUser);
                const groupText = formatBreachGroupCell(userResolution, t);

                return (
                  <TableRow key={breach.id}>
                    {/* Time/User/Location/Group render below - column set and order are
                        frozen at four (PRD/design D10 copy freeze plus the 2026-09-15 User
                        column addition; PRD OQ4's signed order). `breach.durationSeconds` IS
                        available on `WorkZoneBreach` (design section 6; PRD D4) and would
                        satisfy the "show seconds below 120s, don't round a 31-second run to
                        'about a minute'" requirement, but it is deliberately NOT surfaced as
                        a column in M4 - adding one would violate D10. Left here, unused, for
                        whenever the layout is revisited. */}
                    <TableCell>
                      {/* Local time is the primary, always-visible text (2026-09-15 reversal
                          - see the module header's Time paragraph); the UTC-formatted string
                          moves to this hover tooltip rather than disappearing. */}
                      <Tooltip title={formatWorkZoneBreachTime(breach.startTsUtc)} placement="top">
                        <span>{formatWorkZoneBreachLocalTime(breach.startTsUtc)}</span>
                      </Tooltip>
                    </TableCell>
                    {/* User (added 2026-09-15, M4 user-attribution feature) - resolved via
                        the two-source lookup above. NEVER `breach.authId` itself - see this
                        file's header comment (PRD D8/R14/S7). */}
                    <TableCell>{userText}</TableCell>
                    <TableCell>
                      {/* mapUrl === null (never a falsy check on the location itself) - a
                          present location with a genuine 0 latitude/longitude still yields a
                          non-null URL and must still render as a link, not plain text. */}
                      {mapUrl === null ? (
                        locationText
                      ) : (
                        <Link
                          href={mapUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={openInMapsLabel}
                          title={openInMapsLabel}
                        >
                          {locationText}
                        </Link>
                      )}
                    </TableCell>
                    {/* Group (AMENDED 2026-09-15 - the original comment below claimed this
                        cell "always renders empty" and warned against fixing it; that claim
                        is now FACTUALLY WRONG and is amended rather than deleted, per the
                        same "wrong developer documentation is a defect" reasoning the prior
                        PRD's D10 used to authorize updating this exact comment before.
                        Original claim, preserved: there is no group/crew/team field anywhere
                        in the telemetry WIRE payload - the identity set the wire actually
                        carries is org_code / auth_id / c_mac / c_hw_id / is_core / cfg_id /
                        schema_ver, and nothing else. That remains true and is exactly why
                        Group is still NEVER derived from telemetry itself.

                        What changed: Group is now populated from `getUsersGroup(organization,
                        authId)` - an EXTERNAL membership source read from the raw
                        `organization.groups` document, not a telemetry-side proxy. The
                        binding prohibition is UNCHANGED and remains fully in force: do NOT
                        derive a value here from cfg_id, device clustering, co-location, or
                        any other telemetry-side proxy. This cell now renders `groupText`
                        (never `''`, never the raw `authId` - PRD D8/R14/S7) rather than
                        staying empty; a future "helpful" fix that swaps this back to a
                        telemetry-derived shortcut would still be exactly the fabrication the
                        original warning existed to prevent.

                        FIXED 2026-09-16: `groupText` no longer falls back to either of the
                        User column's own two strings when the group half is empty - see
                        `formatBreachGroupCell`'s doc comment above for the production bug
                        this corrects (a Group cell that could literally read "No user
                        recorded"). */}
                    <TableCell>{groupText}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        {canToggle && (
          <Button
            variant="text"
            size="small"
            disableRipple
            onClick={() =>
              setExpandedIdentity((prev) => (prev === rowSetIdentity ? null : rowSetIdentity))
            }
            data-testid="work-zone-adapt-show-more"
            sx={{
              mt: 0.5,
              px: 0,
              minWidth: 0,
              // `px:0`/`minWidth:0` read as a text link, but MUI's default text-Button hover
              // still paints a tinted box + `translateY` lift, reading as a stray rectangle.
              // Plain underline + color-darken instead, so hover is unmistakably a link.
              '&:hover': {
                backgroundColor: 'transparent',
                color: 'primary.dark',
                textDecoration: 'underline',
                transform: 'none',
              },
            }}
          >
            {expanded
              ? t(StringKeys.INSIGHTS_WORK_ZONE_ADAPT_SHOW_LESS)
              : t(StringKeys.INSIGHTS_WORK_ZONE_ADAPT_SHOW_MORE, {
                  count: hiddenCount,
                })}
          </Button>
        )}
        </>
      )}
    </SectionCard>
  );
}
